import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ArticleIcon from '@mui/icons-material/Article';
import TableChartIcon from '@mui/icons-material/TableChart';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import InfoIcon from '@mui/icons-material/Info';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import { useAdminLanguage } from './AdminI18n';

function Documentation() {
  const { t } = useAdminLanguage();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        const response = await fetch('/api/documentation');
        if (response.ok) {
          const data = await response.json();
          setDocuments(data);
        } else {
          // Documents par défaut si l'API n'existe pas encore
          setDocuments([
            {
              id: 1,
              name: 'Guide utilisateur BlueNote',
              description: 'Manuel complet pour les utilisateurs de BlueNote',
              type: 'pdf',
              size: '2.4 MB',
              version: '1.0',
              updatedAt: '2026-01-15'
            },
            {
              id: 2,
              name: 'Guide administrateur',
              description: "Documentation pour l'administration de BlueNote",
              type: 'pdf',
              size: '1.8 MB',
              version: '1.0',
              updatedAt: '2026-01-10'
            },
            {
              id: 3,
              name: 'Modèle de rubriques',
              description: 'Template pour créer des rubriques personnalisées',
              type: 'docx',
              size: '156 KB',
              version: '1.2',
              updatedAt: '2026-01-20'
            },
            {
              id: 4,
              name: 'Liste des codes infractions',
              description: 'Référentiel des codes infractions utilisables',
              type: 'xlsx',
              size: '890 KB',
              version: '2.1',
              updatedAt: '2025-12-05'
            },
            {
              id: 5,
              name: 'Package rubriques standard',
              description: 'Ensemble de rubriques prédéfinies à importer',
              type: 'zip',
              size: '4.2 MB',
              version: '3.0',
              updatedAt: '2026-02-01'
            }
          ]);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des documents:', error);
        // Documents par défaut en cas d'erreur
        setDocuments([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, []);

  const getFileIcon = (type) => {
    switch (type.toLowerCase()) {
      case 'pdf':
        return <PictureAsPdfIcon color="error" />;
      case 'docx':
      case 'doc':
        return <ArticleIcon color="primary" />;
      case 'xlsx':
      case 'xls':
        return <TableChartIcon color="success" />;
      case 'zip':
      case 'rar':
        return <FolderZipIcon color="warning" />;
      default:
        return <DescriptionIcon color="action" />;
    }
  };

  const getTypeChip = (type) => {
    const colors = {
      pdf: 'error',
      docx: 'primary',
      doc: 'primary',
      xlsx: 'success',
      xls: 'success',
      zip: 'warning',
      rar: 'warning'
    };
    return (
      <Chip 
        label={type.toUpperCase()} 
        size="small" 
        color={colors[type.toLowerCase()] || 'default'}
        variant="outlined"
      />
    );
  };

  const handleDownload = async (document) => {
    try {
      const response = await fetch(`/api/documentation/download/${document.id}`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = window.document.createElement('a');
        a.href = url;
        a.download = `${document.name}.${document.type}`;
        window.document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      } else {
        console.error('Erreur lors du téléchargement');
      }
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* En-tête principal */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 1, display: 'flex', alignItems: 'center' }}>
          <LibraryBooksIcon sx={{ mr: 2, fontSize: 40 }} />
          {t('Documentation')}
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          {t('Téléchargez les documents et guides relatifs à BlueNote')}
        </Typography>
      </Box>

      {/* Bandeau d'information */}
      <Accordion elevation={3} sx={{ mb: 4, borderRadius: 2, boxShadow: 3, background: 'rgb(230, 241, 248)', '&:before': { display: 'none' } }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon color="info" />}
          aria-controls="info-content"
          id="info-header"
          sx={{ py: 1 }}
        >
          <InfoIcon color="info" sx={{ mr: 1 }} />
          <Typography variant="h6" color="info.dark" sx={{ fontWeight: 'bold' }}>
            {t('Informations')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ py: 2 }}>
          <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
            {t('Cette page regroupe tous les documents utiles pour utiliser et administrer BlueNote.')}<br/>
            {t('Vous y trouverez des guides utilisateur, des modèles de documents et des ressources complémentaires.')}<br/>
            {t('Cliquez sur le bouton de téléchargement pour obtenir le document souhaité.')}<br/>
            {t("Pour toute question concernant ces documents, contactez le support à l'adresse : contact@pyll.be")}
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* Tableau des documents */}
      <Card elevation={3}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <DescriptionIcon color="primary" sx={{ mr: 1 }} />
            {t('Documents disponibles')}
          </Typography>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : documents.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                {t('Aucun document disponible pour le moment')}
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table sx={{ minWidth: 650 }} aria-label="table des documents">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'action.hover' }}>
                    <TableCell sx={{ fontWeight: 'bold', width: 60 }}>{t('Type')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t('Nom du document')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t('Description')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: 100 }}>{t('Version')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: 100 }}>{t('Taille')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: 80 }} align="center">{t('Action')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {documents.map((doc) => (
                    <TableRow 
                      key={doc.id}
                      sx={{ 
                        '&:hover': { backgroundColor: 'action.hover' },
                        '&:last-child td, &:last-child th': { border: 0 }
                      }}
                    >
                      <TableCell>
                        {getFileIcon(doc.type)}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {doc.name}
                          </Typography>
                          {getTypeChip(doc.type)}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {doc.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={`v${doc.version}`} 
                          size="small" 
                          variant="outlined"
                          color="info"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {doc.size}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={t('Télécharger')}>
                          <IconButton 
                            color="primary" 
                            onClick={() => handleDownload(doc)}
                            sx={{ 
                              '&:hover': { 
                                backgroundColor: 'primary.light',
                                color: 'primary.contrastText'
                              }
                            }}
                          >
                            <DownloadIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default Documentation;
